import React, { useCallback, useState, useEffect, useRef } from 'react'
import { InfoSheetPanel } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import zionDebug from '@fs/zion-debug'
import { trackEvent } from '@fs/zion-analytics'
import {
  generateSls,
  getNestedOrFloatingElementById,
  saveSLSActions,
  useSlsDispatchContext,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { errorCollector } from '../../../../../../shared/utils/utils'
import { headers, R2AppName } from '../../../../../../shared/services/config'
import ErrorOverlay from '../../../../../../shared/components/errorOverlay/ErrorOverlay'
import { useR2DispatchContext, useR2StateContext } from '../../../../../providers/r2Provider/R2Context'
import { useOverlayContext } from '../../../../../../shared/providers/overlayContextProvider/OverlayContext'
import SharedFieldFooter from './SharedFieldFooter'
import SharedFieldPage1 from './SharedFieldPage1'
import SharedFieldPage2 from './SharedFieldPage2'

const debug = zionDebug('rip:R2:EditSharedField')

const SharedField = () => {
  const { rip_sharedImageViewer_saveChanges: saveChangesFlag } = useFeatureFlags(['rip_sharedImageViewer_saveChanges'])
  const r2State = useR2StateContext()
  const state = useSlsStateContext()
  const [t] = useTranslation()
  const [sharedFieldValue, setSharedFieldValue] = useState()
  const [saving, setSaving] = useState()
  const [reducerActions, setReducerActions] = useState([])
  const slsDispatch = useSlsDispatchContext()
  const r2Dispatch = useR2DispatchContext()
  const [saveError, setSaveError] = useState()
  const { topMostPanel, openOverlay, closeOverlay, editSharedFieldPage1Overlay, editSharedFieldPage2Overlay } =
    useOverlayContext()
  const { setActiveFieldInfo, clearEdits, commitEdits } = useSlsHighlightsContext()
  const saveButtonRef = useRef(null)

  const sharedElement = getNestedOrFloatingElementById(state.images, r2State.editingSharedFieldId)

  // this is needed in order for changes to be registered (copied and modified from AddPerson.js)
  useEffect(() => {
    if (reducerActions?.length > 0) {
      slsDispatch({
        type: 'BATCH_DISPATCHES',
        value: reducerActions,
      })
      setReducerActions([])
    }
  }, [slsDispatch, reducerActions])

  const successfulSaveCleanup = useCallback(
    (dispatches) => {
      setReducerActions(dispatches)
      closeOverlay('editSharedFieldPage1')
      closeOverlay('editSharedFieldPage2')
      setActiveFieldInfo({ id: null, value: null })
      commitEdits()
      r2Dispatch({ type: 'SET_HIGHLIGHTS', action: r2State.editHighlights })
    },
    [setActiveFieldInfo, closeOverlay, commitEdits, r2Dispatch, r2State.editHighlights]
  )

  const saveSls = useCallback(
    (slsActions, dispatches) => {
      if (saveChangesFlag.isOn) {
        saveSLSActions({
          actions: slsActions,
          userId: r2State.userId,
          appName: R2AppName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
        })
          .then(() => {
            successfulSaveCleanup(dispatches)
          })
          .catch((err) => {
            setSaveError(err)
            errorCollector(err, null, {
              codeId: 78,
              slsActions,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          })
          .finally(() => {
            setSaving(false)
          })
      } else {
        successfulSaveCleanup(dispatches)
        debug('Saving changes to SLS is disabled', dispatches, slsActions)
        setSaving(false)
      }
    },

    [saveChangesFlag.isOn, r2State?.userId, successfulSaveCleanup]
  )

  const handleSubmitSharedSurname = useCallback(
    (values) => {
      if (topMostPanel() === 'editSharedFieldPage1') {
        openOverlay('editSharedFieldPage2')
        setSharedFieldValue(values[sharedElement.id])
      } else {
        trackEvent({
          event_name: 'shared_field_edit',
          link_name: `Edit: Shared Field: Save`,
        })
        const dispatches = values
        setSaving(true)
        const slsActions = generateSls(dispatches)
        saveSls(slsActions, dispatches)
      }
    },
    [topMostPanel, sharedElement, saveSls, openOverlay]
  )

  // The back arrow on panel 2 was clicked
  const userClosedPage2Panel = () => {
    closeOverlay('editSharedFieldPage2')
  }

  // The back arrow on panel 1 was clicked
  const userClosedPage1Panel = () => {
    closeOverlay('editSharedFieldPage1')

    clearEdits() // Clear the edits from the highlights hook when the user goes back
    // This resets the blue selection highlights on the image
    r2Dispatch({ type: 'REVERT_HIGHLIGHTS', value: {} })
  }

  return (
    <>
      <InfoSheetPanel
        title={t('shared-field.title', 'Edit Linked {fieldType}', { fieldType: sharedElement?.typeData?.displayLabel })}
        headingColor="red"
        {...{ ...editSharedFieldPage1Overlay, close: userClosedPage1Panel }}
        footer={<SharedFieldFooter topMostPanel={topMostPanel} saveButtonRef={saveButtonRef} />}
      >
        <SharedFieldPage1
          handleSubmit={handleSubmitSharedSurname}
          editSharedFieldPage1Overlay={editSharedFieldPage1Overlay}
          onDiscard={userClosedPage1Panel}
        />
      </InfoSheetPanel>
      <InfoSheetPanel
        title={t('shared-field.title', 'Edit Linked {fieldType}', { fieldType: sharedElement?.typeData?.displayLabel })}
        headingColor="red"
        {...{ ...editSharedFieldPage2Overlay, close: userClosedPage2Panel }}
        footer={<SharedFieldFooter topMostPanel={topMostPanel} saving={saving} />}
      >
        <SharedFieldPage2
          sharedFieldValue={sharedFieldValue}
          handleSave={handleSubmitSharedSurname}
          editSharedFieldPage2Overlay={editSharedFieldPage2Overlay}
          saveButtonRef={saveButtonRef}
          saving={saving}
          onDiscard={() => {
            userClosedPage1Panel()
            userClosedPage2Panel()
          }}
        />
      </InfoSheetPanel>

      <ErrorOverlay
        error={saveError}
        setError={setSaveError}
        title={t('save-error-overlay.heading', 'Unable to Save Changes')}
        message={t('save-error-overlay.content-generic', 'Your changes were not saved.  Please try again later.')}
      />
    </>
  )
}

export default SharedField
